/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { Wrapper } from '@nile/components/src/Components/CreatableDropdown/CreatableDropdown';
import { OptionWrapper } from '@nile/components/src/Components/CreatableDropdown/components/EditAndDeleteOption';
import { Wrap } from '@nile/components/src/Components/InlineEdit/Components/InlineDependantSelect';
import { StyledTag } from '@nile/components/src/Components/InlineEdit/Components/InlineSelect';

import { Badge } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';

// components
import {
  getNotificationTemplateByConstantHook,
  getTranslatedText
} from '@nile/components';
import OutsideClickListener from './components/OutsideClickListener';

// helpers
import { getFilteredOptions } from './helpers';

// const Wrapper = styled.div`
//   .popover {
//     background-color: #fff;
//     transform: translateY(calc(100% + 5px));
//     z-index: 100;
//   }
//   .option-list {
//     max-height: 200px;
//   }
// `;

// const Wrap = styled.div`
//   max-width: 100%;
//   min-height: 38px;
//   .edit-actions {
//     visibility: hidden;
//     cursor: pointer;
//   }
// `;

// const StyledTag = styled.div`
//   background: var(--sops-light-blue);
// `;

// const OptionWrapper = styled.div`
//   min-height: 45px;
//   background-color: ${(props) => (props.selected ? '#0B0114' : 'transparent')};
//   color: ${(props) => (props.selected ? '#fff' : '#000')};
//   &:hover {
//     background-color: ${(props) =>
//       props.selected && !props.editState ? '#0B0114' : '#F1F6FF'};
//   }
//   &.focused {
//     background-color: ${(props) =>
//       props.selected && !props.editState ? '#0B0114' : '#F1F6FF'};
//   }
// `;

const StatusDropdown = ({
  options,
  editable,
  selectedOption,
  updateSelectedProperties,
  disabled = false,
  mode,
  hideTag,
  skipListener,
  menuClassName
}) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(mode !== 'read');
  const [textValue, setTextValue] = useState('');
  const [currentFocus, setCurrentFocus] = useState(0);
  const notificationTemplateByConstant = getNotificationTemplateByConstantHook({
    notificationTemplateType: 'SYSTEM_TICKETING',
    constant: 'TICKET_REQ_CLOSED'
  });

  const wrapperRef = useRef();

  const textFieldDisabled =
    disabled || options.reduce((acc, { editState }) => acc || editState, false);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const updateSelection = (val) => {
    if (updateSelectedProperties) {
      updateSelectedProperties({
        status: val.value
      });
      sessionStorage.removeItem('closeTicketClicked');
      sessionStorage.removeItem('suppressCloseNotificationClicked');
      if (val?.details?.constants === 'CLOSE') {
        sessionStorage.setItem('closeTicketClicked', true);
      }
    }
  };

  const selectOptions = (option, closeMenuAfterSelect = true) => {
    updateSelection(option);
    if (closeMenuAfterSelect) {
      closeMenu();
      // remove focus from input
      document.activeElement.blur();
    }
  };

  useEffect(() => {
    if (currentFocus > getFilteredOptions(options, textValue)?.length - 1) {
      setCurrentFocus(0);
    }
  }, [currentFocus, options, textValue]);

  const handleKeyDown = (e) => {
    const size = getFilteredOptions(options, textValue)?.length;
    if (e.keyCode === 40) {
      // Down arrow
      e.preventDefault();
      setCurrentFocus(currentFocus >= size - 1 ? 0 : currentFocus + 1);
    } else if (e.keyCode === 38) {
      // Up arrow
      e.preventDefault();
      setCurrentFocus(currentFocus === 0 ? size - 1 : currentFocus - 1);
    }

    if (e.key === 'Enter') {
      // Enter
      const focusedOption = getFilteredOptions(options, textValue)[
        currentFocus
      ];
      if (focusedOption) {
        selectOptions(focusedOption);
      }
    }
  };

  if (mode === 'read') {
    return (
      <Wrap
        className={`${
          editable ? 'editable-cell' : ''
        } flex center fw full-height inlineSelect`}
      >
        {hideTag ? (
          <div
            className="ellipsis self-center fg1 flex center full-height rounded-4px uppercase font-weight-600 width-text-with-icon"
            title={selectedOption?.label}
          >
            {selectedOption?.label}
          </div>
        ) : (
          <StyledTag
            className="ellipsis self-center fw flex center full-height p4 pl10 pr10 rounded-4px uppercase font-weight-600"
            title={selectedOption?.label}
          >
            {selectedOption?.label}
          </StyledTag>
        )}
        {editable && (
          <div className="edit-actions">
            <div className="ml8 fx center">
              <SVGIcon name="Edit" />
            </div>
          </div>
        )}
      </Wrap>
    );
  }

  return (
    <Wrapper
      className="fw flex fcol position-relative noborder nooutline"
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      subtlePlaceholder={!selectedOption?.label}
    >
      <span className="editable-cell fx actr">
        <Wrap
          onClick={() => {
            if (textFieldDisabled) return;
            if (!menuOpen) {
              setMenuOpen(true);
            } else {
              closeMenu();
            }
          }}
          className="flex center full-height fw inlineSelect"
        >
          {hideTag ? (
            <div
              className="ellipsis self-center fg1 flex center full-height rounded-4px uppercase font-weight-600"
              title={selectedOption?.label}
            >
              {selectedOption?.label}
            </div>
          ) : (
            <StyledTag
              className="ellipsis self-center flex center full-height p4 pl10 pr10 rounded-4px uppercase font-weight-600"
              title={selectedOption?.label}
            >
              <div className="ellipsis">{selectedOption?.label}</div>
            </StyledTag>
          )}
        </Wrap>
        <span className="mlauto">
          <SVGIcon name="ArrowDown" />
        </span>
      </span>
      {menuOpen && (
        <div
          className={`popover card-border bshadow fw br-5 p8 pr0 position-absolute b0 l0 scroll ${menuClassName ||
            ''}`}
        >
          <div className="flex fcol fhp">
            <div className="flex fcol f11 scroll option-list">
              {getFilteredOptions(options, textValue).map((option, index) => {
                return (
                  <OptionWrapper
                    style={{ minWidth: 'auto' }}
                    className="flex center jspbtwn rounded-4px ptr p8 pr12 pl-12 select-option"
                    selected={option.value === selectedOption.value}
                  >
                    <span
                      onClick={(e) => {
                        updateSelection(option);
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      className="fg1 select-option"
                    >
                      {option?.label}
                    </span>
                    {notificationTemplateByConstant?.activated &&
                      option?.details?.constants &&
                      option?.details?.constants.includes('CLOSE') && (
                        <span
                          onClick={() => {
                            updateSelectedProperties({
                              status: option.value,
                              suppressCloseNotification: true
                            });
                            sessionStorage.removeItem('closeTicketClicked');
                            sessionStorage.setItem(
                              'suppressCloseNotificationClicked',
                              true
                            );
                          }}
                          className="fx actr mlauto ptr select-option"
                        >
                          <Tooltip
                            html={
                              <div className="p3 f12">
                                {getTranslatedText(
                                  intl,
                                  'ticket.action.closeWithoutNotification'
                                )}
                              </div>
                            }
                            delay={400}
                            duration={250}
                            position="bottom"
                            distance={10}
                            inertia
                            trigger="mouseenter"
                            animation="shift"
                            animateFill={false}
                          >
                            <Badge
                              radial
                              status="attention"
                              // status={cancelNotify ? 'attention' : 'secondary'}
                              icon={
                                <SVGIcon
                                  name="NotificationsBellCrossed"
                                  size="xs"
                                />
                              }
                            />
                          </Tooltip>
                        </span>
                      )}
                  </OptionWrapper>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {!skipListener && (
        <OutsideClickListener
          reference={wrapperRef}
          onOutsideClick={() => {
            setTextValue('');
            closeMenu();
          }}
        />
      )}
    </Wrapper>
  );
};

const OptionShape = {
  value: PropTypes.string,
  label: PropTypes.string,
  id: PropTypes.string
};

StatusDropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape(OptionShape)).isRequired,
  selectedOption: PropTypes.arrayOf(PropTypes.shape(OptionShape)).isRequired
};

export default StatusDropdown;
